import _                        from 'underscore';
import React, { FC, ReactNode } from 'react';
import { List }                 from 'semantic-ui-react';

import { IAuditRecordTypeQuery, useAuditRecords } from '../../../hooks';
import { CorvalAuditEntry, Uuid }                 from '../../../types';
import { AUDIT_RECORD_TYPE }                      from '../../../constants/SystemRecordEnums';
import { AuditMemo, AuditMemoProps }              from './AuditMemo';
import { AuditMemoStatementProps }                from './AuditMemoStatement';

type PassThroughMemoPropKeys =
	Pick<AuditMemoStatementProps,
		'preamble' |
		'actionDesc' |
		'actionVerb' |
		'auditSubject' |
		'auditAdditionalInfo' |
		'userLabel' |
		'includeTimestampInMessage' |
		'timestampFormat'> &
	Pick<AuditMemoProps,
		'organizationId' |
		'includeAvatar' |
		'render'>;

interface AuditMemoListProps extends Omit<AuditMemoProps, 'record' | 'includeAvatar'>, IAuditRecordTypeQuery {
	
	/* Component to wrap the content in as a container */
	wrapperComponent?: FC<{ records: CorvalAuditEntry[] }>,
	
	/* Memo props specified for each audit type; can be explicit as a typed value, or each memo component prop key can have a function that has each audit record passed to it.
	```
	{
		[ auditType ]: {
	 	    preamble:                   (auditRecord) => string,
	 	    actionDesc:                 (auditRecord) => string,
	 	    actionVerb:                 (auditRecord) => string,
	 	    auditSubject:               (auditRecord) => string,
	 	    auditAdditionalInfo:        (auditRecord) => string,
	 	    userLabel:                  (auditRecord) => string,
	 	    includeTimestampInMessage:  (auditRecord) => boolean,
	 	    timestampFormat:            (auditRecord) => string,
	 	    organizationId:             (auditRecord) => Uuid,
	 	    includeAvatar:              (auditRecord) => boolean,
	 	    render:                     (auditRecord) => ReactNode,
	    }
	}
	```
	Useful for when a list of audit records are intermingled */
	memoPropsByType?: Partial<Record<AUDIT_RECORD_TYPE, Partial<Record<keyof PassThroughMemoPropKeys, string | boolean | ( ( record: CorvalAuditEntry ) => string | ReactNode )>>>>
}

const defaultProps: AuditMemoListProps = {
	records:          [],
	types:            [],
	memoPropsByType:  {},
	timeBoundary:     null,
	entityIds:        null,
	accountIds:       null,
	wrapperComponent: null,
}

/* Component to list AuditMemo child components based on criteria, either as a default message or a custom rendered item */
const AuditMemoList: FC<AuditMemoListProps> = ( props ) => {
	
	// No records or types? Bail.
	if( _.isEmpty( props.records ) ) return null;
	
	// Find record types
	let { getAuditRecordTypes } = useAuditRecords(),
	    records                 = getAuditRecordTypes(
		    props.records,
		    props.types,
		    props.timeBoundary,
		    props.entityIds,
		    props.accountIds,
	    );
	
	// No records returned? Bail...
	if( _.isEmpty( records ) ) return null;
	
	let WrapperComponent      = props.wrapperComponent ?? React.Fragment as FC,
	    wrapperComponentProps = props.wrapperComponent ? { records } : {};
	
	let hasMemoPropsByType = _.keys( props.memoPropsByType ).length > 0;
	
	// ...otherwise display list
	return (
		<WrapperComponent { ...wrapperComponentProps }>
			<List className={ 'no-padding' }>
				{ records.map( ( auditEntry ) => {
					
					let memoProps             = {},
					    auditTypeMemoProps    = props.memoPropsByType[ auditEntry.auditType ] ?? null,
					    auditTypeHasMemoProps = _.isObject( auditTypeMemoProps );
					
					// customize the memos for each type based on our type props
					// run a function on the audit entry, or just return the value back
					if( hasMemoPropsByType && auditTypeHasMemoProps ){
						memoProps = _.mapObject( auditTypeMemoProps, ( value, key ) => {
							return ( _.isFunction( value ) ) ? value( auditEntry ) : value;
						} );
					}
					
					return (
						<List.Item key={ auditEntry.id }>
							<AuditMemo
								record={ auditEntry }
								{ ..._.omit( props, 'records', 'types', 'timeBoundary', ) }
								{ ...memoProps }
							/>
						</List.Item>
					);
					
				} ) }
			</List>
		</WrapperComponent>
	);
};

AuditMemoList.defaultProps = defaultProps;

export { AuditMemoList }
